/**
 * REQ-59 demo・preview では、デプロイがあったら**前の版を出さずに、新しい版で開く**。
 *
 * PWA の Service Worker は、ページを開く要求に端末に保存した前の版で答え、新しい版は裏で取る。
 * 取れても今の画面は読み直さないので、デプロイの後に初めて開いた 1 回は前の版が出る。
 * demo は 1 日に十数回デプロイされるため、検証の人が前の版を見て「直っていない」と返すことが
 * 続いていた (版の番号を見て確かめる運用は、検証の人が意識しないので成り立たない)。
 *
 * **デプロイの検知には sw.js を使う** (別の版の番号は作らない)。sw.js はデプロイのたびに変わる
 * (保存するファイルの一覧が入っている)。開いたときに sw.js が変わったかを確かめ、済むまで画面を
 * 出すのを待ち (holdUntilLatest)、
 * - 新しい版が無ければ、保存した版をそのまま出す (待つのは確認の 1 往復だけ)
 * - 新しい版があれば、**前の版は出さずに**読み込み中のまま待つ。新しい版の保存が終わって
 *   Service Worker が替わったとき (controllerchange) に読み直し、新しい版で開く
 *
 * 前の版を出してから切り替えると、数秒は前の画面が見える (検証の人がそれで判断しうる) うえ、
 * 前の版が先に動く (QR のスタンプの読み取りを前の版が送ってしまう) ので、出す前に待つ。
 *
 * - 初めて開いたとき (前の版が無い) は待たない。その画面は最新
 * - 店の画面 (/store/, /staff/) は待たず、読み直しもしない。この 2 つはもともと開くたびにサーバから
 *   取っていて (vite.config.ts の navigateFallbackDenylist)、開いた時点で最新。開きっぱなしの
 *   タブレットを操作の途中で読み直すことになるので避ける
 * - 本番・体験 (prod / trial) では何もしない。本番のデプロイは月 1 回ほどで、イベントの来場者は
 *   ほとんどが初めて開く人 (前の版が無い)。待たせる・読み直すことによる不便の方が大きい
 * - 確かめるのも新しい版を待つのも上限を決める (電波が悪くても、いつまでも読み込み中にしない)。
 *   上限を過ぎたら保存した版で開き、新しい版の準備ができた時点で読み直す
 * - サーバへの問い合わせは、demo・preview で開くたびに sw.js の確認が 1 本増えるだけ (変わって
 *   いなければ 304 で 0 バイト)。ブラウザも開いたときに同じ確認をしているが、その結果 (新しい版が
 *   無かった) は画面からは受け取れないので、自分で確かめる
 */
const CHECK_TIMEOUT_MS = 3_000 // 新しい版があるかの確認 (sw.js の 304) を待つ上限
const INSTALL_TIMEOUT_MS = 15_000 // 新しい版の保存を待つ上限

function enabled(): boolean {
  const env = import.meta.env.VITE_APP_ENV
  if (env !== 'demo' && env !== 'preview') return false
  if (typeof navigator === 'undefined' || !('serviceWorker' in navigator)) return false
  return !/^\/(store|staff)\//.test(window.location.pathname)
}

const sleep = (ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms))

/** この画面を前の版の Service Worker が受け持っている (= 前の版で開いた) か */
let openedWithSavedVersion = false

if (enabled()) {
  openedWithSavedVersion = !!navigator.serviceWorker.controller
  navigator.serviceWorker.addEventListener('controllerchange', () => {
    // 初めて保存したときにも来るが、その画面は最新なので読み直さない
    if (!openedWithSavedVersion) return
    // 開いた後にアプリの中で店の画面へ移っていることもあるので、読み直す時点の URL でも見る
    if (/^\/(store|staff)\//.test(window.location.pathname)) return
    window.location.reload()
  })
}

/** 画面を出す前に待つ必要があるか (false なら、今までどおりすぐ画面を出す) */
export function shouldHoldForLatest(): boolean {
  return enabled() && openedWithSavedVersion
}

/**
 * 画面を出す前に呼ぶ。新しい版が無いと分かったら (または上限を過ぎたら) 解決する。
 * 新しい版があれば、入れ替わりで読み直す (controllerchange) ので、それまで解決しない。
 */
export async function holdUntilLatest(): Promise<void> {
  if (!shouldHoldForLatest()) return
  try {
    const reg = await navigator.serviceWorker.getRegistration()
    if (!reg) return
    await Promise.race([reg.update(), sleep(CHECK_TIMEOUT_MS)])
    if (!reg.installing && !reg.waiting) return
    await sleep(INSTALL_TIMEOUT_MS)
  } catch {
    // オフラインなど。保存した版で開く
  }
}
